import {Directive, Input, TemplateRef, ViewContainerRef, OnInit, Optional, OnDestroy, ChangeDetectorRef} from '@angular/core';
import {ReplaySubject, Subject} from 'rxjs';
import {UserPermissionsService} from '../services/user-permissions.service';
import { takeUntil} from 'rxjs/operators';
import {AbstractRoleProvider} from '../../requests/classes/role-provider';

/**
 * @usageNotes
 *
 * Use exportsAs and isAllowed properties of this directive to disable component
 * @example
 * ```
 * <div [appUserPermissions]="'permissionName'"
 *   #ref="appUserPermissions">
 *   <input [disabled]="ref.isAllowed">
 * </div>
 *
 * To remove from template a component inaccessible due to permissions use directive
 * on ng-template component
 * @example
 * <ng-template [appUserPermissions]="''">
 *  <div>...</div>
 *  <div>...</div>
 * </ng-template>
 * ```
 * Content of ng-template will not be rendered
 */

@Directive({
  selector: '[appUserPermissions]',
  exportAs: 'appUserPermissions'
})
export class UserPermissionsDirective implements OnInit, OnDestroy {
  public isAllowed: boolean;

  private userPermissionsSubject = new ReplaySubject<string[]>(1);
  private ngUnsubscribe = new Subject<void>();

  constructor(private viewContainer: ViewContainerRef,
              private userPermissionsService: UserPermissionsService,
              private cd: ChangeDetectorRef,
              @Optional() private roleProvider: AbstractRoleProvider,
              @Optional() private templateRef?: TemplateRef<any>,
  ) {
  }

  @Input() set appUserPermissions(permission: string | string[]) {
    const arrayPermission = [permission].flat();
    this.userPermissionsSubject.next(arrayPermission);
  }

  ngOnInit() {
    this.userPermissionsSubject
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((permission) => this.checkUserPermissions(permission));
  }

  private async checkUserPermissions(permission: string | Array<string>) {
    const isAllowed = await this.userPermissionsService.isActionAvailable(permission, this.roleProvider);

    if (isAllowed) {
      if (this.templateRef) {
        this.viewContainer.createEmbeddedView(this.templateRef);
      }
      this.isAllowed = true;
    } else {
      if (this.templateRef) {
        this.viewContainer.clear();
      }
      this.isAllowed = false;
    }
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
